import { useSyncExternalStore } from "react";

/**
 * The two layout breakpoints, shared by CSS (`@media (max-width: 760px)` and `(max-width: 1100px)`)
 * and JS. Always test them with matchMedia, never with innerWidth, so both agree at the boundary.
 */
export const PHONE_QUERY = "(max-width: 760px)";
export const NARROW_QUERY = "(max-width: 1100px)";
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
/** A finger is the main pointer: controls need room for a thumb, and there may be no keyboard. */
export const COARSE_POINTER_QUERY = "(pointer: coarse)";

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const m = window.matchMedia(query);
      m.addEventListener("change", onChange);
      return () => m.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/** True on Apple platforms, where the modifier key is ⌘ rather than Ctrl. */
export function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform ?? navigator.platform ?? "";
  return /mac|iphone|ipad|ipod/i.test(platform);
}

/** A shortcut written the way the platform writes it: "⌘\" or "⇧⌘\" on Apple, "Ctrl+\" or "Ctrl+Shift+\" elsewhere. */
export function shortcutLabel(key: string, shift = false): string {
  if (isApplePlatform()) return `${shift ? "⇧" : ""}⌘${key}`;
  return `Ctrl+${shift ? "Shift+" : ""}${key}`;
}

/** Whether a key event comes from somewhere a person types, where global shortcuts must not fire. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type;
    return !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes(type);
  }
  return false;
}
